import {
  Button,
  Card,
  Icon,
  Input,
  List,
  Modal,
  notification,
  Spin,
  Select,
  Tabs
} from 'antd';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import React, { Dispatch, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isEmail } from 'validator';
import BaseLayout from '../../../components/BaseLayout/BaseLayout';
import CustomDivider from '../../../components/CustomDivider';
import ChangePassword from '../../../components/Profile/ChangePassword';
import RedeemCode from '../../../components/Profile/RedeemCode';
import { PAYPAL_URL } from '../../../env';
import { useWindowSize } from '../../../hooks/WindowsSize';
import { Customer, CustomerCard, User } from '../../../interfaces';
import { AppState, sagaMiddleware } from '../../../store';
import {
  getCustomerInfo,
  updateCustomerCard,
  deleteCustomerCard
} from '../../../store/sagas/customer';
import {
  deactivateAccount,
  guestToUser,
  unsubscribePlan,
  updateAccount
} from '../../../store/sagas/user';
import './ProfilePage.scss';
import AddCardModal from '../../../components/AddCardModal/AddCardModal';
import ManageChildrenPage from '../ManageChildrenPage/ManageChildrenPage';
import PurchaseHistory from '../PurchaseHistory/PurchaseHistory';
import PinkCircle from '../../../components/Icons/PinkCircle';


const { confirm } = Modal;
const { TabPane } = Tabs;

interface Props {
  customer: Customer;
  user: User;
}

export interface UserSettings {
  first?: string;
  last?: string;
  email?: string;
}

const ProfilePage: React.FC<Props> = ({ customer, user }) => {
  const [width] = useWindowSize();
  const [loading, setLoading] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [addCard, setAddCard] = useState(false);
  const [modalPassword, setModalPassword] = useState(false);
  const [modalCode, setModalCode] = useState(false);
  const [settings, setSettings] = useState<UserSettings>({});
  const [oldStripeId, setOldStripeId] = useState('');
  const [card, setCard] = useState('');
  const history = useHistory();

  useEffect(() => {
    setCard(customer.default_source || '');
  }, [customer]);

  useEffect(() => {
    if ( card !== customer.default_source && card !=="" ) {
      changeDefaultCard(card)
    }
  }, [card]);

  useEffect(() => {
    setSettings({
      first: user.first_name,
      last: user.last_name,
      email: user.email
    });

    if (user.stripe_id !== oldStripeId && !!user.stripe_id) {
      setOldStripeId(user.stripe_id);
      setLoading(true);
      sagaMiddleware.run<any>(getCustomerInfo, (error?: string) => {
        setLoading(false);

        if (!!error) {
            Modal.error({
              title: "Error",
              content: error,
            });  
        }
      });
    }
  }, [user, oldStripeId]);

  const saveSettings = useCallback(
    (e: React.FormEvent<HTMLFormElement>, settings: UserSettings) => {
      e.preventDefault();
      setLoading(true);

      sagaMiddleware.run<any>(
        updateAccount,
        settings,
        (err?: string, description?: string) => {
          setLoading(false);

          if (!!err) {
            Modal.error({
              title: 'Error',
              content: err
            });
          } else if (!!description) {
            notification.success({
              message: 'Success',
              description
            });
          }
        }
      );
    },
    []
  );

  const desactivateAccount = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      confirm({
        title: 'Are you sure you want to delete your BabyPage account?',
        content: 'You will lose access to your content, photos and BabyPages',
        onOk() {
          setLoading(true);

          sagaMiddleware.run<any>(deactivateAccount, (err?: string) => {
            setLoading(false);
            if (!!err) {
              if (err === "In order to deactivate your account, please first unsubscribe from BabyPage Plus." ) {
                const error = err.replace("deactivate", "delete");
                Modal.warn({
                  title: 'Warning',
                  content: error,
                })
              }  else {
                Modal.error({
                  title: 'Error',
                  content: err
                });
              }     
            }
          });
        }
      });
    },
    []
  );

  const changeUserLevel = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      confirm({
        title: 'Would you like to create your own BabyPage account?',
        content: 'With an account you can create your own BabyPages and books',
        onOk() {
          setLoading(true);

          sagaMiddleware.run<any>(
            guestToUser,
            user.id as number,
            (err?: string, data?: any) => {
              setLoading(false);
              if (!!err) {
                Modal.error({
                  title: 'Error',
                  content: err
                });
              } else if (!!data) {
                history.push('/step-2');
              }
            }
          );
        }
      });
    },
    [user.id, history]
  );

  const unsubscribe = useCallback(
    (body: { planId: string; subsId: string }) => {
      confirm({
        title: 'Are you sure you want to unsubscribe from your plan?',
        onOk() {
          setLoading(true);

          sagaMiddleware.run<any>(unsubscribePlan, body, (err?: string) => {
            setLoading(false);

            if (!!err) {
              Modal.error({
                title: 'Error',
                content: err
              });
            }
            else {
              notification.success({
                message: 'Success',
                description: 'We are sorry to see you go. You will have access to PLUS features until the end of your current billing cycle.'
              });
            }
          });
        }
      });
    },
    []
  );

  const changeDefaultCard = useCallback((cardToken: string) => {
    confirm({
      title: 'Are you sure you want to change subscription card?',
      onOk() {
        setLoading(true);

        sagaMiddleware.run<any>(
          updateCustomerCard,
          cardToken,
          (err?: string) => {
            setLoading(false);

            if (!!err) {
              Modal.error({
                title: 'Error',
                content: err
              });
            } else {
              customer.default_source = cardToken;
              notification.success({
                message: 'Success',
                description: 'Card changed successfully'
              });
            }
          }
        );
      },
      onCancel() {
        setCard(customer.default_source || '');
      }
    });
  }, []);

  const deleteCard = useCallback((cardToken: string) => {
    confirm({
      title: 'Are you sure you want to remove this card?',
      onOk() {
        setLoading(true);

        sagaMiddleware.run<any>(
          deleteCustomerCard,
          cardToken,
          (err?: string) => {
            setLoading(false);

            if (!!err) {
              Modal.error({
                title: 'Error',
                content: err
              });
            } else {
              notification.success({
                message: 'Success',
                description: 'Card removed successfully'
              });
            }
          }
        );
      }
    });
  }, []);

  const getNameSubscription = useCallback((name: string) => {
    switch (name) {
      case 'monthly':
        return 'Monthly Membership';
      case 'three month':
        return 'Quarterly Membership';
      case 'six month':
        return 'Semesterly Membership';
      case 'one year':
      default:
        return 'Annual Membership';
    }
  }, []);

  const renderMember = useCallback(
    () => (
      <Card className="d-block profile-card my-md-3 my-0 px-3 dynamic-color-card">
        <h2>MEMBERSHIP STATUS</h2>
        <h3 className='mt-3 mb-2'>
          {!!user.user_level && user.stripe_subscriptions ? `BabyPage ${getNameSubscription(user.stripe_subscriptions.name)}` : !!user.user_level && user.in_app_purchase ? 
          user.in_app_purchase.title : "FREE MEMBER"}
        </h3>
        {!!user.in_app_purchase || !!user.stripe_subscriptions ? (
          <>
            <p className="m-0">
              {!!user.in_app_purchase
                ? `Your Subscription will renew on ${user.in_app_purchase?.expires_date.slice(0, 10)}.`
                : `Your Subscription will renew on ${user.stripe_subscriptions?.current_period_end.slice(0, 10)}.`
              }
            </p>
            {!!user.in_app_purchase ? (
              <>
                {user.in_app_purchase.status === "grace-period" &&
                !!user.in_app_purchase.cancelation_date ? (
                  <p>
                    You unsubscribed from your BabyPage PLUS account on{" "}
                    {moment(user.in_app_purchase.cancelation_date).format(
                      "MM[/]DD[/]YYYY"
                    )}
                    . Please reactivate your PLUS account before it expires on{" "}
                    {moment(user.in_app_purchase.expires_date).format(
                      "MM[/]DD[/]YYYY"
                    )}{" "}
                    so you don't lose access to the content you created with
                    PLUS features.
                  </p>
                ) : null
                }
                <div className="text-center my-2">
                  <Button
                  className='w-100 green-button dashboard-button my-3'
                  shape='round'
                  disabled={loading}
                  >
                    <a
                      href="https://buy.itunes.apple.com/WebObjects/MZFinance.woa/wa/manageSubscriptions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="n"
                    >
                      Cancel Subscription
                    </a>
                  </Button>
                </div>
              </>
            ) : !!user.stripe_subscriptions!.stripe_id!! ? (
              <Button
                className='w-100 green-button dashboard-button my-3'
                shape='round'
                onClick={(e) =>
                  unsubscribe({
                    planId: user.stripe_subscriptions!.name,
                    subsId: user.stripe_subscriptions!.stripe_id!,
                  })
                }
                disabled={loading}
              >
                Cancel subscription
              </Button>
            ) : (
              <a
                href={PAYPAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="BigLink"
              >
                Manage Subscription
              </a>
            )}
          </>
        ) : (
          <>
            <p>
              Upgrade to PLUS and get FREE shipping,
              10% off books, premium designs, covers and more. Only $3.75 per month with an annual membership!
            </p>
            <Button         
                ghost
                shape="round"
                className="w-100 green-button dashboard-button"
                onClick={() => {
                    history.push("/store")
                }}
              >
                Upgrade to PLUS + 
              </Button>
          </>
        )}
      </Card>
    ),
    [user, getNameSubscription, loading, unsubscribe]
  );

  const renderProfile = useCallback(
    () => (
      <>
        <Card className="d-block profile-card my-md-3 my-0 px-3">
          <h2>PROFILE SETTINGS</h2>
          <form onSubmit={e => saveSettings(e, settings)}>
            <div className="row justify-content-center align-items-center">
              <div className="col-12 my-1">
                <label>First Name</label>
                <Input
                  type="text"
                  className="w-100"
                  value={settings.first || ''}
                  maxLength={30}
                  onChange={e => {
                    let value = e.target.value;
                      setSettings(state => ({
                        ...state,
                        first: value
                      }))
                    }
                  }
                />
              </div>
              <div className="col-12 my-1">
                <label>Last Name</label>
                <Input
                  type="text"
                  className="w-100"
                  value={settings.last || ''}
                  maxLength={30}
                  onChange={e => {
                    let value = e.target.value;
                      setSettings(state => ({
                        ...state,
                        last: value
                      }))
                   } 
                }
                />
              </div>
              <div className="col-12 my-1">
                <label>Email</label>
                <Input
                  type="email"
                  className="w-100"
                  value={settings.email || ''}
                  maxLength={60}
                  onChange={e => {
                      let value = e.target.value;
                      setSettings(state => ({
                        ...state,
                        email: value,
                      }))
                    }
                  }
                />
              </div>
              <div className="col-12 my-1">
                <Button
                  htmlType="submit"
                  shape="round"
                  className="w-100 mt-3 green-button dashboard-button"
                  disabled={
                    loading ||
                    !settings.first ||
                    !settings.last ||
                    !settings.email ||
                    !isEmail(settings.email)
                  }
                >
                  Update Settings
                </Button>
              </div>
              <div className="my-2">
                <p
                  className='clickable-profile-text'
                  onClick={() => setModalPassword(true)}
                >
                  Change Password
                </p>
              </div>
            </div>
          </form>
        </Card>
      </>
    ),
    [settings, loading, saveSettings]
  );

  const renderGift = useCallback(
    () => (
      <>
        <Card className="d-block profile-card my-md-3 my-0 px-3">
          <div className="d-flex flex-column justify-content-center align-items-center">
          <h2 >GIFT CARD BALANCE</h2>
          <p className="m-0 mt-3 mb-2">
            {!!customer.bp_balance
              ? `You have $${customer.bp_balance.toFixed(2)} left on your giftcard.`
              : 'You have no gift card balance'}
          </p>
          <p
            className='clickable-profile-text'
            onClick={() => setModalCode(true)}
          >
              Redeem Code
          </p>
          </div>    
        </Card>
      </>
    ),
    [customer.bp_balance]
  );

  const renderDeactivate = useCallback(
    () => (
      <>
        <Card className="d-block profile-card px-3">
          <h2 className="">DELETE ACCOUNT</h2>
          <p>
            Once you delete your account, you will no longer have access to
            any of your content, photos or BabyPages
          </p>
          <Button
            type="ghost"
            shape="round"
            className="w-100 mx-auto white-button dashboard-button" 
            disabled={loading}
            onClick={desactivateAccount}
          >
            Delete account
          </Button>
        </Card>
      </>
    ),
    [loading, desactivateAccount]
  );

  const renderChangeUserLevel = useCallback(
    () => (
      <div className="d-flex flex-column justify-content-center mx-md-5 mt-4">
        <h2 className="message">
          Would you like to create your own BabyPage account?
        </h2>
        <Button 
          size="small"
          shape="round"
          className="py-3 mx-auto"
          disabled={loading}
          onClick={changeUserLevel}
        >
          YES
        </Button>
      </div>
    ),
    [loading, changeUserLevel]
  );

  const renderCards = useCallback(() => {
    let cardList = null;

    if (
      !!customer.sources &&
      !!customer.sources.data &&
      !!customer.sources.data.length
    ) {
      let cards = cloneDeep(customer.sources.data);
      const defaultIndex = cards.findIndex(
        c => c.id === customer.default_source
      );

      if (defaultIndex > 0) {
        cards.splice(0, 0, cards.splice(defaultIndex, 1)[0]);
      }

      cardList = (
        <>

            { customer.sources.data.length > 0 && (
              <div className="row justify-content-center align-items-end">
                <div className='w-100 position-relative select-wrapper'>
                  <div className="prefix-icon-wrapper"><PinkCircle /></div>
                  <Select value={card} onChange={setCard} className="w-100 card-select">
                    
                    {customer.sources.data.map((c: CustomerCard, index) => (
                      <Select.Option key={index} value={c.id}>
                        &#x25cf;&#x25cf;&#x25cf;&#x25cf; &#x25cf;&#x25cf;&#x25cf;&#x25cf; &#x25cf;&#x25cf;&#x25cf;&#x25cf; {`${c.last4}`}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
          <List
            itemLayout="horizontal"
            dataSource={showCards ? cards : [cards[0]]}
            renderItem={(item: CustomerCard) => (
              <List.Item
                actions={[
                  <Button
                    type="danger"
                    shape="circle"
                    icon="delete"
                    onClick={() =>
                      !user.stripe_subscriptions ||
                      !user.stripe_subscriptions.stripe_id ||
                      customer.sources!.data!.length > 1
                        ? deleteCard(item.id!)
                        : Modal.error({
                            title: 'Error',
                            content:
                              'You cannot have an account without cards while you have an active subscription'
                          })
                    }
                    disabled={loading}
                  />
                ]}
              >
                <List.Item.Meta
                  title={
                    !!item.name
                      ? item.name.length > 25
                        ? item.name.substr(0, 22) + '...'
                        : item.name
                      : ''
                  }
                  description={`${item.brand} - ****${item.last4 ||
                    item.dynamic_last4 ||
                    ''}`}
                />
              </List.Item>
            )}
          />
        </>
      );
    }

    return (
      <>
        <Card className="d-block profile-card my-md-3 my-0 px-3">
          <h2>PAYMENT METHODS</h2>
          {cardList || (
            <h3 className="text-center mt-3 mb-2 payment-methods">You don't have any payment methods</h3>
          )}
          <div className="row justify-content-center align-items-center">
            {!!customer.sources &&
              !!customer.sources.data &&
              customer.sources.data.length > 1 && (
                <div className="mx-3">
                  <p
                  className='clickable-profile-text'
                  onClick={() => setShowCards(!showCards)}
                >
                  {showCards ? "Hide" : "Show All"} Payment Methods
                </p>
                </div>
              )}
            <div
              className="my-2"
            >
                <p
                  className='clickable-profile-text'
                  onClick={() => setAddCard(true)}
                >
                  Add Payment Method
                </p>
            </div>
          </div>
        </Card>
      </>
    );
  }, [
    card,
    customer.default_source,
    customer.sources,
    user.stripe_subscriptions,
    loading,
    showCards,
    changeDefaultCard,
    deleteCard
  ]);

  return (
    <BaseLayout title="BabyPage - Profile" pageTitle="my account" hideChildMenu pageStyle={{backgroundColor:'#f0f0f0'}}>
        <div className="profile-tabs">
          <div className="tabs-wrapper">       
            <Tabs defaultActiveKey={history.location.search.includes("child") ? "children" : "profile"} tabBarGutter={true ? 60 : 0} className="mb-2" >
              <TabPane tab="Profile Settings" key={"profile"}                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                    >
              {user.user_level === 3 && renderChangeUserLevel()}
              <div className="mx-md-5 my-md-3 my-0">
                {loading && (
                  <div className="col-12 d-flex justify-content-center">
                    <Spin
                      indicator={<Icon type="loading" style={{ fontSize: 24 }} spin />}
                    />
                  </div>
                )}
                {user.user_level === 3 ? (
                  <div className="row justify-content-center align-items-center m-0">
                    <div className="col-12 col-md-6 col-profile">{renderProfile()}</div>
                    <div className="col-12 col-md-6 col-profile">
                      {renderDeactivate()}
                    </div>
                  </div>
                ) : width >= 768 ? (
                  <div className="row justify-content-center">
                    <div className="col-lg-6 col-xl-5 col-xxl-4 col-md-6">
                      <div className="row justify-content-center ">
                        <div className="col-12 col-profile">{renderMember()}</div>
                        <div className="col-12 col-profile">{renderCards()}</div>
                        <div className="col-12 col-profile">{renderGift()}</div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-xl-5 col-xxl-4 col-md-6">
                      <div className="row justify-content-center align-items-center ">
                        <div className="col-12 col-profile">{renderProfile()}</div>
                        <div className="col-12 col-profile">{renderDeactivate()}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="row justify-content-center align-items-center m-0">
                    <div className="col-12 col-profile">{renderMember()}</div>
                    <div className="col-12 col-profile">{renderProfile()}</div>
                    <div className="col-12 col-profile">{renderCards()}</div>            
                    <div className="col-12 col-profile">{renderGift()}</div>
                    <div className="col-12 col-profile">{renderDeactivate()}</div>
                  </div>
                )}
              </div>
              <ChangePassword
                visible={modalPassword}
                cancel={() => setModalPassword(false)}
                finishSuccess={(description: string) => {
                  setModalPassword(false);
                  notification.success({
                    message: 'Success',
                    description
                  });
                }}
              />
              <RedeemCode
                visible={modalCode}
                cancel={() => setModalCode(false)}
                finishSuccess={(description: string) => {
                  setModalCode(false);
                  notification.success({
                    message: 'Success',
                    description
                  });
                }}
              />
              <AddCardModal visible={addCard} close={() => setAddCard(false)} />
              </TabPane>
              <TabPane tab="Manage Children" key={"children"} style={{backgroundColor: '#f0f0f0'}} > 
                <ManageChildrenPage />  
              </TabPane>
              <TabPane tab="Purchase History" key={"purchase"} >
                <PurchaseHistory />
              </TabPane>
            </Tabs>
          </div>
        </div>
    </BaseLayout>
  );
};

const mapStateToProps = (state: AppState) => ({
  customer: state.customer,
  user: state.user
});

export default connect(mapStateToProps)(ProfilePage);
